"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "uk";

type Dict = Record<string, string>;

const EN: Dict = {
  "app.kicker": "Horosho Capital",
  "app.title": "Global Macro Terminal",
  "app.brand": "Horosho Capital",
  "app.brand.sub": "Market Intelligence",
  "search.modules": "Search modules",
  "btn.collect": "Collect live",
  "btn.capture": "Run capture job",
  "collector.run": "Run collector",
  "collector.run_short": "Run",
  "collector.running": "Collector running",
  "collector.running_short": "Running",
  "collector.started": "Collector started. The daily schedule is unchanged.",
  "collector.complete": "Collector finished. Dashboard data was refreshed.",
  "collector.failed": "Collector failed",
  "collector.already_running": "A scheduled or manual collector run is already in progress.",
  "collector.separate_schedule": "Run the Browserbase collector now without changing the daily schedule",
  "nav.overview": "Overview",
  "nav.manufacturing": "Manufacturing",
  "nav.markets": "Markets",
  "nav.crypto": "Crypto",
  "nav.macro": "Macro",
  "nav.system-status": "System Status",
  "nav.research": "Research",
  "nav.sources": "Sources",
  "card.cb": "Global CB Signal",
  "card.cycle": "Manufacturing Cycle",
  "card.etf": "ETF Flow Pulse",
  "card.vol": "BTC Vol Surface",
  "card.fresh": "Data Freshness",
  "mfg.ism": "ISM PMI Manufacturing",
  "mfg.indicators": "Required Indicators",
  "mfg.cycle": "Cycle vs Credit Impulse",
  "mfg.formula": "Formula And Diffusion",
  "mkt.perfmap": "ETF / Asset Performance Map",
  "mkt.fomc": "FOMC Statement Index",
  "mkt.watchlist": "Markets Watchlist",
  "mkt.etfflows": "ETF Flows",
  "cb.composite": "Central Bank Composite",
  "cb.forward": "FED Forward Rate Curve",
  "cb.ois": "OIS To Current Rate",
  "cb.score": "weighted score from -2 to +2",
  "crypto.btc": "BTC Spot",
  "research.title": "Research And News",
  "sources.policy": "Data Source Policy",
  "sources.pipeline": "Discovery Pipeline",
  "job.live": "Live collection",
  "job.capture": "Capture job",
  "job.sources": "live source(s)",
  "freshness.live": "LIVE",
  "pwa.update.aria": "Application update",
  "pwa.update.title": "Update available",
  "pwa.update.detail": "Reload when you are ready to use the latest Horosho release.",
  "pwa.update.reload": "Reload now",
  "pwa.update.later": "Later",
  "pwa.update.dismiss": "Dismiss update",
  "pwa.install.aria": "Install Horosho",
  "pwa.install.title": "Install Horosho",
  "pwa.install.detail": "Open the macro terminal as a dedicated app on this device.",
  "pwa.install.action": "Install app",
  "pwa.install.dismiss": "Dismiss install guidance",
  "pwa.ios.title": "Add Horosho to your Home Screen",
  "pwa.ios.detail": "Tap Share, then Add to Home Screen."
};

const UK: Dict = {
  "app.kicker": "Horosho Capital",
  "app.title": "Global Macro Terminal",
  "app.brand": "Horosho Capital",
  "app.brand.sub": "Ринкова аналітика",
  "search.modules": "Пошук модулів",
  "btn.collect": "Зібрати наживо",
  "btn.capture": "Запустити збір",
  "collector.run": "Запустити колектор",
  "collector.run_short": "Запуск",
  "collector.running": "Колектор працює",
  "collector.running_short": "Працює",
  "collector.started": "Колектор запущено. Щоденний розклад не змінено.",
  "collector.complete": "Колектор завершив роботу. Дані дашборда оновлено.",
  "collector.failed": "Помилка колектора",
  "collector.already_running": "Плановий або ручний запуск колектора вже виконується.",
  "collector.separate_schedule": "Запустити Browserbase-колектор зараз, не змінюючи щоденний розклад",
  "nav.overview": "Огляд",
  "nav.manufacturing": "Виробництво",
  "nav.markets": "Ринки",
  "nav.crypto": "Крипто",
  "nav.macro": "Макро",
  "nav.system-status": "Стан системи",
  "nav.research": "Дослідження",
  "nav.sources": "Джерела",
  "card.cb": "Сигнал центробанків",
  "card.cycle": "Виробничий цикл",
  "card.etf": "Потоки в ETF",
  "card.vol": "Волатильність BTC",
  "card.fresh": "Актуальність даних",
  "mfg.ism": "ISM PMI Виробництво",
  "mfg.indicators": "Необхідні індикатори",
  "mfg.cycle": "Цикл vs Кредитний імпульс",
  "mfg.formula": "Формула та дифузія",
  "mkt.perfmap": "Карта показників ETF / активів",
  "mkt.fomc": "Індекс заяв FOMC",
  "mkt.watchlist": "Список спостереження",
  "mkt.etfflows": "Потоки ETF",
  "cb.composite": "Зведений сигнал центробанків",
  "cb.forward": "Форвардна крива ставки ФРС",
  "cb.ois": "OIS до поточної ставки",
  "cb.score": "зважений бал від -2 до +2",
  "crypto.btc": "Спот BTC",
  "research.title": "Дослідження та новини",
  "sources.policy": "Політика джерел даних",
  "sources.pipeline": "Конвеєр виявлення",
  "job.live": "Збір наживо",
  "job.capture": "Збір через браузер",
  "job.sources": "живих джерел",
  "freshness.live": "НАЖИВО",
  "pwa.update.aria": "Оновлення застосунку",
  "pwa.update.title": "Доступне оновлення",
  "pwa.update.detail": "Перезавантажте, коли будете готові перейти на останню версію Horosho.",
  "pwa.update.reload": "Перезавантажити",
  "pwa.update.later": "Пізніше",
  "pwa.update.dismiss": "Закрити оновлення",
  "pwa.install.aria": "Встановити Horosho",
  "pwa.install.title": "Встановити Horosho",
  "pwa.install.detail": "Відкривайте макротермінал як окремий застосунок на цьому пристрої.",
  "pwa.install.action": "Встановити",
  "pwa.install.dismiss": "Закрити підказку",
  "pwa.ios.title": "Додайте Horosho на початковий екран",
  "pwa.ios.detail": "Натисніть «Поділитися», потім «На початковий екран»."
};

const DICTS: Record<Lang, Dict> = { en: EN, uk: UK };

type I18nValue = { lang: Lang; setLang: (lang: Lang) => void; t: (key: string) => string };
const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, initialLang = "en" }: { children: ReactNode; initialLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    window.localStorage.setItem("lang", next);
    document.cookie = `hc_lang=${next}; Path=/; Max-Age=31536000; SameSite=Lax`;
    document.documentElement.lang = next;
  }, []);

  const t = useCallback((key: string) => DICTS[lang][key] ?? DICTS.en[key] ?? key, [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) return { lang: "en", setLang: () => {}, t: (key: string) => EN[key] ?? key };
  return ctx;
}
